'use client';

import { useContext } from 'react';
import { ProductPurchaseOption } from 'content/products';
import { M1ProductData } from 'content/products/m1/IN';
import {
  CurrencySymbol,
  useFormatPrice,
} from 'hooks/pricing/useFormattedPrice';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { M1SelectionContext } from './M1SelectionProvider';
import { Currency } from 'utils/Currency';
import { useSearchParams } from 'next/navigation';
import { getCampaignPromoDiscountConfig } from 'content/referral';
import { campaignDiscountAmount } from 'lib/pricing/displayedPrice';
import { isM1UhxBundlingEnabled } from 'content/products/m1/m1UhxBundlingConfig';
import { ultrahumanXDiscountCodes } from 'content/ultrahumanX';
import styles from './Product.module.css';

export const Product = ({
  className,
  product,
  onClick,
  currency,
  selected,
}: {
  product: M1ProductData;
  currency: Currency;
  selected: boolean;
  onClick: () => void;
  className?: string;
}) => {
  const { productOption, locale } = useContext(M1SelectionContext);
  const {
    affiliateDetails,
    campaignActive,
    store,
    getCampaignForProduct,
    ultrahumanXAdded,
  } = useCartStore(
    useShallow((s) => ({
      affiliateDetails: s.affiliateDetails,
      campaignActive: s.campaignActive,
      store: s.store,
      getCampaignForProduct: s.getCampaignForProduct,
      ultrahumanXAdded: s.ultrahumanXAdded,
    })),
  );
  const searchParams = useSearchParams();
  const formatPriceWithComma = useFormatPrice();

  const effectiveStoreForAffiliate = getEffectiveStoreForDiscount(store);

  const discountConfig =
    affiliateDetails?.discount_configs[
      effectiveStoreForAffiliate as ShopifyStore
    ]?.M1;

  const showAffiliatePricing =
    !campaignActive &&
    // store !== ShopifyStore.ROW &&
    affiliateDetails &&
    affiliateDetails.code &&
    affiliateDetails.m1Active &&
    productOption === ProductPurchaseOption.oneTimePurchase &&
    discountConfig;

  // Calculate birthday discount for this specific product
  const birthdayDiscount = (() => {
    // Check if birthday discount is active via URL parameters
    const isBirthdayActive =
      typeof window !== 'undefined' &&
      searchParams?.get('affiliateCode') === 'birthday' &&
      searchParams?.get('discount');

    if (!isBirthdayActive) return 0;

    // Extract percentage from discount code (last 2 characters if they're numbers)
    const discountCode = searchParams?.get('discount') as string;
    const lastTwoChars = discountCode.slice(-2);
    const percentage = parseInt(lastTwoChars);
    const discountPercentage =
      !isNaN(percentage) && percentage > 0 && percentage <= 100
        ? percentage
        : 15;

    return product.price * (discountPercentage / 100);
  })();

  // Calculate referral discount for this specific product
  const referralDiscount =
    typeof window !== 'undefined' && searchParams?.get('referral')
      ? product.price * 0.1 // 10% referral discount
      : 0;

  // Calculate affiliate discount for this specific product
  const affiliateDiscount = (() => {
    // Exclude affiliate discount when affiliateCode=birthday (like other products)
    if (
      typeof window !== 'undefined' &&
      searchParams?.get('affiliateCode') === 'birthday'
    ) {
      return 0;
    }

    return discountConfig?.type === 'percentage' && discountConfig.value
      ? (product.price * (discountConfig?.value ?? 0)) / 100
      : discountConfig?.type === 'amount'
      ? discountConfig.value
      : 0;
  })();

  const m1UhxBundlingEnabled = isM1UhxBundlingEnabled(store);

  const campaignDiscount = (() => {
    if (productOption !== ProductPurchaseOption.oneTimePurchase) {
      return 0;
    }

    const m1Campaign = getCampaignForProduct('cgm');
    return campaignDiscountAmount({
      product: 'cgm',
      listPrice: product.price,
      campaigns: m1Campaign ? [m1Campaign] : [],
      fallback: getCampaignPromoDiscountConfig({
        store,
        country: locale,
        product: 'm1',
      }),
    });
  })();

  const uhxDiscount = (() => {
    if (
      !m1UhxBundlingEnabled ||
      !ultrahumanXAdded ||
      !store ||
      productOption !== ProductPurchaseOption.oneTimePurchase
    ) {
      return 0;
    }

    const uhxConfig = ultrahumanXDiscountCodes[store as ShopifyStore];
    if (!uhxConfig?.discount) return 0;

    return product.price * uhxConfig.discount;
  })();

  // Calculate effective discount for this specific product
  const productDiscounts = [
    { type: 'referral', amount: referralDiscount },
    { type: 'affiliate', amount: affiliateDiscount },
    { type: 'birthday', amount: birthdayDiscount },
    // Checkout drops the CGM code while UHX is applied.
    { type: 'campaign', amount: uhxDiscount > 0 ? 0 : campaignDiscount },
    { type: 'uhx', amount: uhxDiscount },
  ];
  const productEffectiveDiscount = productDiscounts.reduce(
    (max, current) => (current.amount > max.amount ? current : max),
    { type: 'affiliate', amount: 0 },
  );

  const showDiscountedPricing =
    showAffiliatePricing || productEffectiveDiscount.amount > 0;

  // Show striked price from product data only if no active discounts are applied
  const showStrikedPrice =
    !showDiscountedPricing &&
    product.strikedPrice &&
    product.strikedPrice > product.price;

  return (
    <button
      onClick={onClick}
      className={`gtm ${styles.root}${className ? ` ${className}` : ''}${
        selected ? ` ${styles.active}` : ''
      }`}
      data-buttontype="select plan"
      data-product={product.displayName || product.title || 'Ultrahuman M1'}
      data-variantId={product.variantId}
    >
      <div>
        <h5 className={styles.title}>
          {product.title}
          {product.tag ? (
            <span className={styles.tag}>{product.tag}</span>
          ) : null}
        </h5>
        {showDiscountedPricing ? (
          <div className={styles['price-wrapper']}>
            <p className={`${styles.price} ${styles.strikethrough}`}>
              {CurrencySymbol[currency]}
              {formatPriceWithComma(product.price)}
            </p>
            <p className={styles.price} data-testid="m1-buy-plan-price">
              {CurrencySymbol[currency]}
              {formatPriceWithComma(
                product.price - productEffectiveDiscount.amount,
              )}
            </p>
          </div>
        ) : showStrikedPrice && product.strikedPrice ? (
          <div className={styles['price-wrapper']}>
            <p className={`${styles.price} ${styles.strikethrough}`}>
              {CurrencySymbol[currency]}
              {formatPriceWithComma(product.strikedPrice)}
            </p>
            <p className={styles.price} data-testid="m1-buy-plan-price">
              {CurrencySymbol[currency]}
              {formatPriceWithComma(product.price)}
            </p>
          </div>
        ) : (
          <p className={styles.price} data-testid="m1-buy-plan-price">
            {CurrencySymbol[currency]}
            {formatPriceWithComma(product.price)}
          </p>
        )}
      </div>
      <div>
        <p className="subtitle1">{product.subtitle1}</p>

        <p className="subtitle2">{product.subtitle2}</p>
      </div>
    </button>
  );
};
